import { apiClient } from '@/core/api/client';
import type { TakePayload } from './takeApi';

/**
 * Feature 110 — MVP 2 client take flow (public, token-identified).
 *
 * The link in the client's email is the same URL for both versions, so the GET
 * returns whichever payload the assessment was stamped with and the flow reads
 * `content.version` to decide where to write. The two versions have nothing in
 * common to share — a discovery layer and a four-point scale versus question
 * keys and 0/1/2 — so the write routes are separate.
 */

export type V2Response = 'PREPARED' | 'IN_PROGRESS' | 'NOT_YET' | 'UNSURE';

export type V2ResponseOption = { value: V2Response; label: string; help: string };

export type DiscoveryOption = { key: string; label: string };

/**
 * When a conditional question applies. Authored on the server and sent here as
 * data, so the browser can reveal a question the moment its trigger is answered
 * without re-implementing the condition — there is one source for the rule.
 */
export type DiscoveryRule = {
  questionId: string;
  anyOf?: string[];
  noneOf?: string[];
};

export type DiscoveryQuestion = {
  id: string;
  text: string;
  select: 'SINGLE' | 'MULTI';
  maxSelections?: number;
  options: DiscoveryOption[];
  purpose: string;
  /** Present on conditionals only; the question applies when any rule matches. */
  showWhen?: DiscoveryRule[];
};

export type EscalationCheck = { id: string; text: string };

export type V2TakeNeed = { id: string; question: string; questionSupporter: string };
export type V2TakePillar = { key: string; name: string; num: number; needs: V2TakeNeed[] };

export type V2TakeContent = {
  discoveryQuestions: DiscoveryQuestion[];
  escalationChecks: EscalationCheck[];
  escalationShowWhen: DiscoveryRule[];
  /** D1 answer → what to call the person at the centre. See {@link questionFor}. */
  personLabels: Record<string, string>;
  /** The same person in the client's own voice, for `{{person_own}}`. Same keys. */
  personOwnLabels: Record<string, string>;
  pillars: V2TakePillar[];
  responseOptions: V2ResponseOption[];
  version: string;
};

/** Answers keyed by question id; a single-select holds a one-entry array. */
export type DiscoveryAnswers = Record<string, string[]>;

export type V2TakePayload = {
  /** The report routes take this, not the token. */
  assessmentId: string;
  /**
   * The advisor is taking this themselves (Feature 118). The completion screen
   * then frames the share step as a preview of what the client is asked, and
   * offers the report instead of account creation — the advisor already has one.
   */
  isSelfTest: boolean;
  clientName: string;
  clientEmail: string;
  /** Picks the sign-in vs create-account handoff (Feature 095 split). */
  hasAccount: boolean;
  advisorName: string;
  status: 'INVITED' | 'IN_PROGRESS' | 'COMPLETED';
  isExpired: boolean;
  content: V2TakeContent;
  savedDiscovery: DiscoveryAnswers;
  savedAnswers: Record<string, V2Response>;
  /** The supporter's typed first name for the person they answer about, for resume. */
  subjectFirstName: string | null;
  sharingWithAdvisor: boolean;
};

/**
 * Which version came back. `discoveryQuestions` exists on every MVP 2 payload
 * and on no V1 one, so it discriminates without trusting the version string to
 * have been set.
 */
export const isV2Payload = (payload: TakePayload | V2TakePayload): payload is V2TakePayload =>
  'discoveryQuestions' in payload.content;

/**
 * Whether a gate rule holds. Mirrors the server, including the part that is
 * easy to get wrong: an unanswered gate is "not yet", not "no", so a conditional
 * stays hidden until its trigger has actually been answered.
 */
export const matchesRule = (answers: DiscoveryAnswers, rule: DiscoveryRule): boolean => {
  const selected = answers[rule.questionId] ?? [];
  if (selected.length === 0) return false;
  if (rule.anyOf && !selected.some((value) => rule.anyOf?.includes(value))) return false;
  if (rule.noneOf && selected.some((value) => rule.noneOf?.includes(value))) return false;
  return true;
};

/** Whether any of a question's gate rules holds; no rules means always shown. */
export const applies = (answers: DiscoveryAnswers, rules?: DiscoveryRule[]): boolean =>
  rules === undefined || rules.some((rule) => matchesRule(answers, rule));

export type SaveV2ProgressBody = {
  discovery?: { questionId: string; optionKeys: string[] }[];
  answers?: { needId: string; response: V2Response }[];
  /** Absent leaves the saved name alone; null clears it. */
  subjectFirstName?: string | null;
};

/** The server's cap and character rule for the subject's first name, mirrored. */
export const SUBJECT_NAME_MAX = 50;
const SUBJECT_NAME_PATTERN = /^(?=.*\p{L})[\p{L}\p{M} '’.-]+$/u;

/**
 * Blank is valid — the field is optional — and is sent as null. The cap is
 * checked here too, not only by the input's maxLength: the simulator restores
 * the name from a localStorage draft, which nothing else bounds.
 */
export const isValidSubjectName = (name: string): boolean => {
  const trimmed = name.trim();
  return (
    trimmed === '' || (trimmed.length <= SUBJECT_NAME_MAX && SUBJECT_NAME_PATTERN.test(trimmed))
  );
};

export async function saveV2Progress(
  token: string,
  body: SaveV2ProgressBody
): Promise<{ status: V2TakePayload['status'] }> {
  const response = await apiClient.patch<{ status: V2TakePayload['status'] }>(
    `/longevity-assessments/take/${encodeURIComponent(token)}/v2`,
    body
  );
  return response.data;
}

export type V2CompletionResult = {
  overallScore: number;
  category: string;
  shareStatus: 'SHARED' | 'NONE';
};

/**
 * Finish: scores, snapshots the priorities and records the sharing choice.
 * Sharing is opt-out — the report reaches the advisor unless the client says no.
 */
export async function completeV2Assessment(
  token: string,
  declineSharing: boolean
): Promise<V2CompletionResult> {
  const response = await apiClient.post<V2CompletionResult>(
    `/longevity-assessments/take/${encodeURIComponent(token)}/v2/complete`,
    { declineSharing }
  );
  return response.data;
}

/** Where the token opens a sentence — the same rule as the server's fillPerson. */
const SENTENCE_START = /(^|[.!?]\s+)$/;

/** The server's `fillToken`, mirrored: the browser fills the take's questions itself. */
const fillToken = (text: string, token: RegExp, label: string): string =>
  text.replace(token, (_token, offset: number) =>
    SENTENCE_START.test(text.slice(0, offset))
      ? label.charAt(0).toUpperCase() + label.slice(1)
      : label
  );

/**
 * The wording of a readiness question for this client.
 *
 * Each need is sent with both wordings because the form is served before D1 is
 * answered — the same reason the escalation gate is sent as data. `personLabels`
 * decides both halves: a D1 answer it holds means the client is answering about
 * someone else, and its value is what to call them. So the server keeps the copy
 * and the choice of wording stays a lookup here.
 *
 * Replacement is global: one need names the person twice. Where the person
 * opens the sentence the label's first letter is raised, as the server does.
 * `{{person_own}}` takes `personOwnLabels` — "my parent" — in the statements
 * written in the client's own voice. A typed `subjectName` replaces both.
 */
export const questionFor = (
  need: V2TakeNeed,
  answers: DiscoveryAnswers,
  personLabels: Record<string, string>,
  personOwnLabels: Record<string, string>,
  subjectName?: string | null
): string => {
  const label = personLabelFor(answers, personLabels, subjectName);
  if (label === undefined) return need.question;
  const ownLabel = personLabelFor(answers, personOwnLabels, subjectName) ?? label;
  return fillToken(
    fillToken(need.questionSupporter, /\{\{person_own\}\}/g, ownLabel),
    /\{\{person\}\}/g,
    label
  );
};

/**
 * What to call the person the client is answering about, or undefined when
 * they are answering about themselves. Own keys only: D1 is client state.
 *
 * `subjectName` is the first name a supporter may type. It replaces the
 * relationship outright, as the server's `labelFrom` does; blank keeps the
 * relationship, and answering about themselves it is never used. Its first
 * letter is raised, as the server stores it, so the take reads as the reports.
 */
export const personLabelFor = (
  answers: DiscoveryAnswers,
  personLabels: Record<string, string>,
  subjectName?: string | null
): string | undefined => {
  const d1 = answers.D1?.[0] ?? '';
  if (!Object.prototype.hasOwnProperty.call(personLabels, d1)) return undefined;
  const name = subjectName?.trim();
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : personLabels[d1];
};

/**
 * Drops answers to questions the current answers no longer reveal.
 *
 * Gates are re-evaluated as the client types, so a question can disappear
 * after it was answered — select a recent fall, answer the safety check Yes,
 * then unselect the fall. Without this the Yes stays in state, is saved, and
 * goes on promoting a priority to Now from a check the client can no longer
 * see, let alone change.
 *
 * Only conditionals and escalation checks are ever dropped; the ten required
 * questions have no gate and are always kept.
 */
export const pruneHiddenAnswers = (
  answers: DiscoveryAnswers,
  questions: DiscoveryQuestion[],
  escalationChecks: EscalationCheck[],
  escalationShowWhen: DiscoveryRule[]
): DiscoveryAnswers => {
  const gated = new Map<string, DiscoveryRule[]>([
    ...questions.flatMap((q) => (q.showWhen ? [[q.id, q.showWhen] as const] : [])),
    ...escalationChecks.map((check) => [check.id, escalationShowWhen] as const),
  ]);

  return Object.fromEntries(
    Object.entries(answers).filter(([id]) => {
      const rules = gated.get(id);
      return rules === undefined || applies(answers, rules);
    })
  );
};
